import { minutosDeExpediente } from './expediente.js';
/**
 * SLA de primeiro contato.
 *
 * Medido sobre o Lead: da entrada até a primeira interação registrada. É o
 * indicador que evita perder Lead por atraso de atendimento.
 *
 * Os quinze minutos são de Expediente, não de relógio de parede. Enquanto Lead
 * nascia só de importação a diferença não aparecia, porque importar é gente
 * clicando em horário de trabalho; com Lead chegando de anúncio a qualquer
 * hora, a contagem contínua faria todo Lead noturno nascer vencido e a fila
 * Atender Agora perderia poder de ordenação. Ver ADR 0008.
 */
export const SLA_PRIMEIRO_CONTATO_MINUTOS = 15;
/** Fração do SLA a partir da qual o Lead entra em risco. */
export const FRACAO_ALERTA_SLA = 2 / 3;
/**
 * Espera a partir da qual a entrada conta como resgate.
 *
 * Uma hora entre o envio do formulário na Meta e a chegada do Lead aqui. Acima
 * disso a demora foi nossa e não há prazo a cobrar de ninguém; abaixo, a
 * Comercial teve como ver o Lead e o prazo vale.
 */
export const ESPERA_QUE_CARACTERIZA_RESGATE_MINUTOS = 60;
/**
 * Decide se um Lead que chega agora nasce marcado como resgatado.
 *
 * A marca passou a depender do **tempo de espera**, e não mais da porta por
 * onde o Lead entrou. Enquanto buscar no Lead Center era um mutirão manual, as
 * duas coisas eram a mesma: quem vinha por ali tinha esperado dias. Com a busca
 * rodando por relógio, elas se separam — a busca passa a trazer em minutos
 * também o Lead recém-enviado, e marcá-lo como resgatado só porque veio por ela
 * esvaziaria o indicador de SLA, que mediria zero Lead para sempre.
 *
 * A conta é de relógio de parede, e não de Expediente como o prazo dos quinze
 * minutos. São perguntas diferentes: o prazo mede atendimento, que só corre com
 * gente trabalhando; esta mede quanto tempo a informação ficou represada do
 * nosso lado, e represamento não tira folga. Um Lead enviado de madrugada e
 * trazido cinco minutos depois não esperou nada — o relógio do SLA dele começa
 * a correr na abertura seguinte, pelo Expediente, como o de qualquer outro.
 */
export function esperaCaracterizaResgate(criadoNaMetaEm, chegouEm) {
    const minutos = (chegouEm.getTime() - criadoNaMetaEm.getTime()) / 60_000;
    // Data futura é relógio dessincronizado entre a Meta e nós, não viagem no
    // tempo: a espera é zero, e o Lead entra normal. Tratar negativo como "muito
    // tempo" inverteria o sinal e resgataria justamente o que acabou de chegar.
    return minutos > ESPERA_QUE_CARACTERIZA_RESGATE_MINUTOS;
}
export const ESTADOS_SLA = ['ATENDIDO', 'NO_PRAZO', 'EM_RISCO', 'ESTOURADO', 'RESGATADO'];
export function avaliarSla({ dataEntrada, primeiroContatoEm, resgatadoEm, agora, }) {
    // O prazo do resgatado nunca correu: a porta esteve fechada do nosso lado, e
    // cobrar da Comercial o que ela não teve como ver seria medir a nossa falha
    // no indicador dela. Por isso ele não gasta minuto nenhum, atendido ou não.
    //
    // RESGATADO descreve apenas quem ainda espera, como NO_PRAZO e ESTOURADO —
    // é a resposta a "por que este não tem prazo a cobrar". Depois do primeiro
    // contato a pergunta passa a ser outra, e ATENDIDO é a resposta útil: numa
    // lista de Leads, essa é a única coluna que diz se alguém já falou com a
    // pessoa. Quem quiser saber de onde o Lead veio olha a data do resgate, que
    // é guardada à parte justamente por não ser um estado de prazo.
    if (resgatadoEm) {
        return {
            estado: primeiroContatoEm ? 'ATENDIDO' : 'RESGATADO',
            minutosDecorridos: 0,
            minutosRestantes: SLA_PRIMEIRO_CONTATO_MINUTOS,
            dentroDoPrazo: true,
        };
    }
    const referencia = primeiroContatoEm ?? agora;
    const minutosDecorridos = minutosDeExpediente(dataEntrada, referencia);
    const minutosRestantes = SLA_PRIMEIRO_CONTATO_MINUTOS - minutosDecorridos;
    const dentroDoPrazo = minutosDecorridos <= SLA_PRIMEIRO_CONTATO_MINUTOS;
    if (primeiroContatoEm) {
        return { estado: 'ATENDIDO', minutosDecorridos, minutosRestantes, dentroDoPrazo };
    }
    const estado = !dentroDoPrazo
        ? 'ESTOURADO'
        : minutosDecorridos >= SLA_PRIMEIRO_CONTATO_MINUTOS * FRACAO_ALERTA_SLA
            ? 'EM_RISCO'
            : 'NO_PRAZO';
    return { estado, minutosDecorridos, minutosRestantes, dentroDoPrazo };
}
export const ROTULO_ESTADO_SLA = {
    ATENDIDO: 'Atendido',
    NO_PRAZO: 'No prazo',
    EM_RISCO: 'Próximo do vencimento',
    ESTOURADO: 'SLA vencido',
    RESGATADO: 'Resgatado',
};
const MINUTOS_POR_HORA = 60;
const MINUTOS_POR_DIA = 24 * MINUTOS_POR_HORA;
/**
 * Quanto tempo o Lead está esperando o primeiro contato, em palavra de gente.
 *
 * Minuto é a unidade certa enquanto o prazo é de quinze — e vira ruído quando
 * a espera passa de um dia: "6847 min sem contato" obriga quem lê a dividir por
 * 1440 no meio da fila para saber que são quase cinco dias.
 *
 * O dia é contado por piso, como idade: quem está esperando há 47 horas ainda
 * não completou dois dias. Arredondar para cima inventaria atraso que não
 * existe justamente na coluna que decide quem atender primeiro.
 */
export function descreverEsperaSemContato(minutos) {
    if (minutos < MINUTOS_POR_DIA)
        return `${minutos} min sem contato`;
    const dias = Math.floor(minutos / MINUTOS_POR_DIA);
    return `${dias} ${dias === 1 ? 'dia' : 'dias'} sem contato`;
}
//# sourceMappingURL=sla.js.map